import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRightLeft, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { apiClient } from '@/lib/api';
import { useCardMovements, useResolveCardMovement, type CardMovement } from '@/hooks/useFuel';
import { DriverSelect, toDriverOption } from '@/components/common/DriverSelect';

export default function CardMovementsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<'pending' | 'resolved' | 'all'>('pending');
  const [openMov, setOpenMov] = useState<CardMovement | null>(null);

  const { data, isLoading } = useCardMovements(statusFilter);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/fuel')} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </Button>
          <ArrowRightLeft className="h-5 w-5 text-amber-500" />
          <h1 className="text-lg font-semibold">{t('fuel.cardMovements')}</h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center gap-2">
          {(['pending', 'resolved', 'all'] as const).map((s) => (
            <Button
              key={s}
              variant={statusFilter === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(s)}
            >
              {t(`fuel.movementStatus.${s}`)}
            </Button>
          ))}
        </div>

        {isLoading ? (
          <div className="text-center text-muted-foreground py-12">{t('common.loading')}</div>
        ) : !data?.length ? (
          <div className="text-center py-16 border border-dashed border-border rounded-md">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500 mb-2" />
            <p className="text-muted-foreground">{t('fuel.movementsEmpty')}</p>
          </div>
        ) : (
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <Th>{t('fuel.movementTable.date')}</Th>
                  <Th>{t('fuel.movementTable.card')}</Th>
                  <Th>{t('fuel.movementTable.assignedTo')}</Th>
                  <Th>{t('fuel.movementTable.actuallyOn')}</Th>
                  <Th>{t('fuel.movementTable.driver')}</Th>
                  <Th className="text-right">{t('fuel.movementTable.sar')}</Th>
                  <Th>{t('fuel.movementTable.type')}</Th>
                  <Th>{t('fuel.movementTable.status')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.map((m) => (
                  <tr key={m.id} className="border-t border-border hover:bg-accent/50 cursor-pointer"
                      onClick={() => setOpenMov(m)}>
                    <Td>{new Date(m.transaction_at).toLocaleString()}</Td>
                    <Td><span className="font-mono text-xs">{m.card_number}</span></Td>
                    <Td>{m.assigned_vehicle_plate ?? '—'}</Td>
                    <Td>{m.actual_vehicle_plate ?? <span className="text-muted-foreground">—</span>}</Td>
                    <Td>{m.actual_driver_name ?? m.provider_driver_name ?? '—'}</Td>
                    <Td className="text-right">{Number(m.total_sar).toLocaleString()}</Td>
                    <Td>
                      <Badge className={typeBadgeColor(m.movement_type)}>{t(`fuel.movementType.${m.movement_type}`)}</Badge>
                    </Td>
                    <Td>
                      {m.status === 'pending' ? (
                        <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/30">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          {t('fuel.movementStatus.pending')}
                        </Badge>
                      ) : (
                        <Badge variant="inactive">{t(`fuel.movementStatus.${m.status}`)}</Badge>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {openMov ? (
        <ResolveSheet movement={openMov} onClose={() => setOpenMov(null)} />
      ) : null}
    </div>
  );
}

function typeBadgeColor(type: string) {
  switch (type) {
    case 'detected': return 'bg-amber-500/15 text-amber-700 border-amber-500/30';
    case 'emergency_swap': return 'bg-blue-500/15 text-blue-700 border-blue-500/30';
    case 'fraud_confirmed': return 'bg-red-500/15 text-red-700 border-red-500/30';
    case 'data_error': return 'bg-gray-500/15 text-gray-700 border-gray-500/30';
    case 'unmapped_card': return 'bg-purple-500/15 text-purple-700 border-purple-500/30';
    default: return 'bg-muted';
  }
}

function ResolveSheet({ movement, onClose }: { movement: CardMovement; onClose: () => void }) {
  const { t } = useTranslation();
  const [moveType, setMoveType] = useState<'emergency_swap' | 'fraud_confirmed' | 'data_error'>('emergency_swap');
  const [actualVehicleId, setActualVehicleId] = useState('');
  const [actualDriverId, setActualDriverId] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);

  const resolve = useResolveCardMovement();

  useEffect(() => {
    apiClient.get('/api/v1/vehicles', { params: { pageSize: 500 } })
      .then(({ data }) => setVehicles(data.items ?? data ?? []));
    apiClient.get('/api/v1/drivers', { params: { pageSize: 500 } })
      .then(({ data }) => setDrivers(data.items ?? data ?? []));
  }, []);

  async function submit() {
    if (!reason.trim()) return alert(t('fuel.resolve.reasonRequired'));
    if (moveType === 'emergency_swap' && !actualVehicleId) {
      return alert(t('fuel.resolve.vehicleRequired'));
    }
    try {
      await resolve.mutateAsync({
        id: movement.id,
        movement_type: moveType,
        actual_vehicle_id: actualVehicleId || null,
        actual_driver_id: actualDriverId || null,
        authorization_reason: reason,
        resolution_notes: notes || null,
      });
      onClose();
    } catch (err: any) {
      alert(err?.response?.data?.message ?? err?.message ?? 'Resolve failed');
    }
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('fuel.resolve.title')}</SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="rounded-md border border-border bg-muted/20 p-3 text-sm space-y-1">
            <div><strong>{t('fuel.resolve.transaction')}:</strong> {Number(movement.total_sar).toLocaleString()} SAR · {Number(movement.liters).toFixed(2)} L</div>
            <div><strong>{t('fuel.resolve.date')}:</strong> {new Date(movement.transaction_at).toLocaleString()}</div>
            <div><strong>{t('fuel.resolve.station')}:</strong> {movement.station_name ?? '—'}</div>
            <div><strong>{t('fuel.resolve.card')}:</strong> <span className="font-mono">{movement.card_number}</span></div>
            <div><strong>{t('fuel.resolve.assignedVehicle')}:</strong> {movement.assigned_vehicle_plate ?? '—'}</div>
          </div>

          <div>
            <label className="text-sm font-medium">{t('fuel.resolve.movementType')}</label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <TypeRadio value="emergency_swap" current={moveType} setCurrent={setMoveType}
                label={t('fuel.movementType.emergency_swap')}
                hint={t('fuel.resolve.emergencyHint')} />
              <TypeRadio value="fraud_confirmed" current={moveType} setCurrent={setMoveType}
                label={t('fuel.movementType.fraud_confirmed')}
                hint={t('fuel.resolve.fraudHint')} />
              <TypeRadio value="data_error" current={moveType} setCurrent={setMoveType}
                label={t('fuel.movementType.data_error')}
                hint={t('fuel.resolve.dataErrorHint')} />
            </div>
          </div>

          {moveType === 'emergency_swap' ? (
            <>
              <div>
                <label className="text-sm font-medium">{t('fuel.resolve.actualVehicle')}*</label>
                <select
                  className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  value={actualVehicleId}
                  onChange={(e) => setActualVehicleId(e.target.value)}
                >
                  <option value="">— {t('common.select')} —</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>{v.plate_en ?? v.plateEn} ({v.code})</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('fuel.resolve.reattributionNote')}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium">{t('fuel.resolve.actualDriver')}</label>
                <DriverSelect
                  value={actualDriverId}
                  onChange={setActualDriverId}
                  drivers={drivers.map(toDriverOption)}
                  noneLabel={`— ${t('common.select')} —`}
                  className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  renderLabel={(d) => `${d.name} (${d.code ?? '—'})${d.iqama ? ` · ${d.iqama}` : ''}`}
                />
              </div>
            </>
          ) : null}

          <div>
            <label className="text-sm font-medium">{t('fuel.resolve.reason')}*</label>
            <textarea
              className="mt-1 w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('fuel.resolve.reasonPlaceholder') as string}
            />
          </div>

          <div>
            <label className="text-sm font-medium">{t('fuel.resolve.notes')}</label>
            <textarea
              className="mt-1 w-full min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">{t('common.cancel')}</Button>
            <Button onClick={submit} disabled={resolve.isPending} className="flex-1 bg-gold text-gold-foreground hover:bg-gold/90">
              {resolve.isPending ? t('common.processing') : t('fuel.resolve.submit')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TypeRadio({ value, current, setCurrent, label, hint }: { value: any; current: any; setCurrent: any; label: string; hint: string }) {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={() => setCurrent(value)}
      className={`text-left rounded-md border p-2 text-xs transition-colors ${
        active ? 'border-gold bg-gold/10 text-foreground' : 'border-border bg-card hover:bg-accent/50'
      }`}
    >
      <div className="font-medium">{label}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>
    </button>
  );
}

function Th({ children, className }: { children: any; className?: string }) {
  return <th className={`px-3 py-2 text-left font-medium text-muted-foreground ${className ?? ''}`}>{children}</th>;
}
function Td({ children, className }: { children: any; className?: string }) {
  return <td className={`px-3 py-2 ${className ?? ''}`}>{children}</td>;
}
